import { useMutation, useQuery, useQueryClient } from "react-query";
import { apiGet, apiPost } from "./api";

interface GenerateParams {
  jobApplicationId: number;
}

const generateAiSummary = async (params: GenerateParams) => {
  return await apiPost({
    path: `/job_applications/${params.jobApplicationId}/ai_job_application_summaries`,
    variables: { aiJobApplicationSummary: params },
  });
};

export function useGenerateAiSummary() {
  const queryClient = useQueryClient();
  return useMutation(generateAiSummary, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["jobApplication", variables.jobApplicationId]);
      queryClient.invalidateQueries(["aiJobApplicationSummary"]);
      queryClient.invalidateQueries(["organizationAiCreditBalance"]);
    },
  });
}

const getAiJobApplicationSummary = async (
  jobApplicationId: number,
  aiJobApplicationSummaryId: number,
) => {
  return await apiGet({
    path: `/job_applications/${jobApplicationId}/ai_job_application_summaries/${aiJobApplicationSummaryId}`,
  });
};

export function useAiJobApplicationSummary({
  jobApplicationId,
  aiJobApplicationSummaryId,
}: {
  jobApplicationId: number;
  aiJobApplicationSummaryId: number | undefined;
}) {
  const queryClient = useQueryClient();
  // Poll every 25s while the summary is still in flight; stop once it reaches a
  // terminal status. react-query 3.13.10 takes refetchInterval as number | false
  // only, so the value is derived from this query's own cached data each render.
  const cachedAiJobApplicationSummary = queryClient.getQueryData<{ status?: string }>([
    "aiJobApplicationSummary",
    aiJobApplicationSummaryId,
  ]);
  const cachedStatus = cachedAiJobApplicationSummary ? cachedAiJobApplicationSummary.status : undefined;
  const refetchInterval =
    cachedStatus !== undefined && cachedStatus !== "succeeded" && cachedStatus !== "failed" ? 25000 : false;
  return useQuery(
    ["aiJobApplicationSummary", aiJobApplicationSummaryId],
    () => getAiJobApplicationSummary(jobApplicationId, aiJobApplicationSummaryId!),
    { enabled: aiJobApplicationSummaryId != undefined, refetchInterval },
  );
}
